<main class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 lg:py-10 overflow-x-hidden">

  <!-- Header -->
  <div class="mb-6">
    <h1 class="tfr-page-heading">Facturas</h1>
    <p class="tfr-page-lead">
      Facturas de comisión que TimeFlexRent te ha emitido. Descárgalas para tu contabilidad.
    </p>
  </div>

  <!-- Filtros + export -->
  <section class="mb-6 rounded-2xl border border-pale-gray bg-white p-4 sm:p-5">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label class="block">
          <span class="text-[11px] font-semibold text-steel-gray">Desde</span>
          <input type="date" [ngModel]="dateFrom()" (ngModelChange)="dateFrom.set($event)"
            class="mt-1 w-full sm:w-auto rounded-xl border border-pale-gray px-3 py-2 text-[13px] text-midnight-indigo" />
        </label>
        <label class="block">
          <span class="text-[11px] font-semibold text-steel-gray">Hasta</span>
          <input type="date" [ngModel]="dateTo()" (ngModelChange)="dateTo.set($event)"
            class="mt-1 w-full sm:w-auto rounded-xl border border-pale-gray px-3 py-2 text-[13px] text-midnight-indigo" />
        </label>
        @if (dateFrom() || dateTo()) {
          <button type="button" (click)="clearFilter()"
            class="text-[12px] font-semibold text-action-blue hover:underline pb-2.5">
            Limpiar
          </button>
        }
      </div>

      <button type="button" (click)="exportCsv()"
        [disabled]="filtered().length === 0"
        class="inline-flex items-center justify-center gap-2 rounded-xl bg-action-blue px-4 py-2.5
               text-[13px] font-semibold text-white transition-colors hover:bg-blue-700
               focus:outline-none focus:ring-2 focus:ring-action-blue/40 disabled:opacity-50">
        <lucide-icon name="download" [size]="15" class="shrink-0" />
        Exportar a Excel (CSV)
      </button>
    </div>
  </section>

  @if (loadError()) {
    <p class="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
      {{ loadError() }}
    </p>
  }

  @if (loading()) {
    <p class="text-[13px] text-steel-gray">Cargando facturas…</p>

  } @else if (rows().length === 0) {
    <div class="flex flex-col items-center justify-center py-20 text-center">
      <div class="h-14 w-14 rounded-2xl flex items-center justify-center bg-cloud-mist mb-4">
        <lucide-icon name="file-text" [size]="24" class="text-steel-gray" />
      </div>
      <p class="text-[14px] font-semibold text-midnight-indigo">Aún no tienes facturas</p>
      <p class="text-[12px] text-steel-gray mt-1.5 max-w-[300px] leading-relaxed">
        Cuando recibas tu primera reserva pagada, aquí aparecerá la factura de comisión correspondiente.
      </p>
    </div>

  } @else {

    <!-- Resumen del rango -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
      <div class="rounded-xl border border-pale-gray bg-white px-4 py-3">
        <p class="text-[10px] font-semibold uppercase tracking-wide text-steel-gray">Facturas</p>
        <p class="text-[20px] font-bold tabular-nums text-midnight-indigo">{{ totals().count }}</p>
      </div>
      <div class="rounded-xl border border-pale-gray bg-white px-4 py-3">
        <p class="text-[10px] font-semibold uppercase tracking-wide text-steel-gray">Base imponible</p>
        <p class="text-[20px] font-bold tabular-nums text-midnight-indigo">
          {{ euros(totals().net) | currency:'EUR':'symbol':'1.2-2':'es' }}
        </p>
      </div>
      <div class="rounded-xl border border-pale-gray bg-white px-4 py-3">
        <p class="text-[10px] font-semibold uppercase tracking-wide text-steel-gray">IVA</p>
        <p class="text-[20px] font-bold tabular-nums text-midnight-indigo">
          {{ euros(totals().vat) | currency:'EUR':'symbol':'1.2-2':'es' }}
        </p>
      </div>
      <div class="rounded-xl border border-pale-gray bg-white px-4 py-3">
        <p class="text-[10px] font-semibold uppercase tracking-wide text-steel-gray">Total</p>
        <p class="text-[20px] font-bold tabular-nums text-midnight-indigo">
          {{ euros(totals().total) | currency:'EUR':'symbol':'1.2-2':'es' }}
        </p>
      </div>
    </div>

    @if (filtered().length === 0) {
      <p class="text-[13px] text-steel-gray">No hay facturas en el rango seleccionado.</p>
    } @else {
      <div class="overflow-x-auto rounded-2xl border border-pale-gray bg-white">
        <table class="min-w-full text-left text-[13px]">
          <thead class="border-b border-pale-gray bg-cloud-mist text-[11px] uppercase tracking-wide text-steel-gray">
            <tr>
              <th class="px-4 py-3 font-medium">Número</th>
              <th class="px-4 py-3 font-medium">Fecha</th>
              <th class="px-4 py-3 font-medium">Concepto</th>
              <th class="px-4 py-3 font-medium text-right">Base</th>
              <th class="px-4 py-3 font-medium text-right">IVA</th>
              <th class="px-4 py-3 font-medium text-right">IRPF</th>
              <th class="px-4 py-3 font-medium text-right">Total</th>
              <th class="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            @for (row of filtered(); track row.id) {
              <tr class="border-b border-pale-gray/60 last:border-0">
                <td class="px-4 py-3 font-mono text-[12px] whitespace-nowrap">{{ row.invoiceNumber }}</td>
                <td class="px-4 py-3 whitespace-nowrap">{{ row.issueDate }}</td>
                <td class="px-4 py-3 max-w-[240px] truncate" [title]="row.concept">{{ row.concept }}</td>
                <td class="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                  {{ euros(row.netCents) | currency:'EUR':'symbol':'1.2-2':'es' }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                  {{ euros(row.vatCents) | currency:'EUR':'symbol':'1.2-2':'es' }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                  {{ euros(row.irpfCents) | currency:'EUR':'symbol':'1.2-2':'es' }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums font-semibold whitespace-nowrap">
                  {{ euros(row.totalCents) | currency:'EUR':'symbol':'1.2-2':'es' }}
                </td>
                <td class="px-4 py-3">
                  <button type="button" (click)="print(row)" [disabled]="printingId() === row.id"
                    class="inline-flex items-center gap-1 rounded-lg border border-pale-gray px-2 py-1 text-[12px] hover:bg-cloud-mist disabled:opacity-50">
                    <lucide-icon name="printer" [size]="14" />
                    PDF
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  }
</main>
